import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Search, Download, FileText, Filter, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { getSheets, captureLead, resolveMedia } from "@/lib/api";
import { toast } from "sonner";

export default function TechnicalSheetsPage() {
  const [searchParams] = useSearchParams();
  const [sheets, setSheets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [systemFilter, setSystemFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [leadWhatsapp, setLeadWhatsapp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getSheets().then(r => { setSheets(r.data); setFiltered(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    let result = sheets;
    if (categoryFilter && categoryFilter !== "all") result = result.filter(s => s.category === categoryFilter);
    if (systemFilter && systemFilter !== "all") result = result.filter(s => s.system_type === systemFilter);
    if (search) result = result.filter(s => s.title.toLowerCase().includes(search.toLowerCase()) || s.description?.toLowerCase().includes(search.toLowerCase()));
    setFiltered(result);
  }, [search, categoryFilter, systemFilter, sheets]);

  const categories = ["General", "METIC", "MPM"];
  const systemTypes = [...new Set(sheets.map(s => s.system_type).filter(Boolean))];

  const categoryStyles = {
    General: "bg-emerald-50 text-emerald-700 border-emerald-200",
    METIC:   "bg-emerald-600 text-white border-emerald-700",
    MPM:     "bg-emerald-400 text-emerald-950 border-emerald-500",
  };

  const handleDownload = (sheet) => {
    setSelectedSheet(sheet);
    setDialogOpen(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadName.trim() || !leadWhatsapp.trim()) {
      toast.error("Complete todos los campos");
      return;
    }
    const cleanNumber = leadWhatsapp.replace(/\D/g, "");
    if (cleanNumber.length < 8) {
      toast.error("Ingrese un número de WhatsApp válido");
      return;
    }
    setSubmitting(true);
    try {
      await captureLead({ name: leadName, whatsapp: cleanNumber, sheet_id: selectedSheet?.id, source: "technical_sheet" });
      toast.success("Ficha técnica disponible para descarga");
      setDialogOpen(false);
      setLeadName("");
      setLeadWhatsapp("");
      const fileUrl = selectedSheet?.file_url;
      if (fileUrl && fileUrl !== "#") {
        window.open(resolveMedia(fileUrl), "_blank", "noopener");
      } else {
        toast.info("Esta ficha aún no tiene PDF disponible. Nuestro equipo le contactará por WhatsApp.");
      }
    } catch {
      toast.error("Error al procesar. Intente de nuevo.");
    }
    setSubmitting(false);
  };

  return (
    <main data-testid="technical-sheets-page" className="pt-20 lg:pt-24 bg-construtop-texture">
      <section className="bg-zinc-950 py-20 lg:py-28 section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-emerald-400 text-sm font-bold uppercase tracking-[0.2em] mb-3">
            Repositorio Técnico
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl text-white mb-4">
            Fichas Técnicas
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-zinc-400 max-w-xl text-base">
            Acceda a toda la documentación técnica de nuestros productos y sistemas de impermeabilización.
          </motion.p>
        </div>
      </section>

      <section className="section-spacing section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 mb-10" data-testid="sheets-filters">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <Input
                data-testid="sheets-search-input"
                placeholder="Buscar ficha técnica..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10 rounded-none border-zinc-300 h-11"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="sheets-category-filter" className="w-full sm:w-48 rounded-none border-zinc-300 h-11">
                <Filter size={16} className="mr-2 text-zinc-400" />
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={systemFilter} onValueChange={setSystemFilter}>
              <SelectTrigger data-testid="sheets-system-filter" className="w-full sm:w-48 rounded-none border-zinc-300 h-11">
                <SelectValue placeholder="Sistema" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los sistemas</SelectItem>
                {systemTypes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-zinc-500 mb-6">{filtered.length} fichas encontradas</div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((sheet, i) => (
              <motion.div
                key={sheet.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-zinc-200 overflow-hidden card-hover"
              >
                <div className="h-48 bg-zinc-50 border-b border-zinc-100 flex items-center justify-center">
                  {sheet.image_url ? (
                    <img
                      src={resolveMedia(sheet.image_url)}
                      alt={sheet.title}
                      className="w-full h-full object-contain p-4"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 text-zinc-300">
                      <FileText size={34} />
                      <span className="text-xs text-zinc-400">Imagen pendiente</span>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                      <FileText size={22} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-zinc-900 text-sm line-clamp-2">{sheet.title}</h3>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 ${categoryStyles[sheet.category] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{sheet.category}</span>
                        {sheet.system_type && <Badge variant="outline" className="text-xs rounded-none">{sheet.system_type}</Badge>}
                      </div>
                    </div>
                  </div>
                  {sheet.description && <p className="text-zinc-500 text-xs line-clamp-2 mb-4">{sheet.description}</p>}
                  <Button
                  data-testid={`download-sheet-${i}`}
                  onClick={() => handleDownload(sheet)}
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-none text-sm font-bold"
                >
                    <Download size={14} className="mr-2" />
                    {sheet.file_url ? "DESCARGAR" : "SOLICITAR PDF"}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <FileText className="mx-auto text-zinc-300 mb-4" size={48} />
              <p className="text-zinc-500">No se encontraron fichas técnicas con los filtros seleccionados.</p>
            </div>
          )}
        </div>
      </section>

      {/* WhatsApp Lead Capture Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="whatsapp-capture-dialog" className="sm:max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Descargar Ficha Técnica</DialogTitle>
            <DialogDescription>
              Ingrese sus datos para acceder a la descarga de: <strong>{selectedSheet?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="lead-name">Nombre completo</Label>
              <Input
                data-testid="lead-name-input"
                id="lead-name"
                value={leadName}
                onChange={e => setLeadName(e.target.value)}
                placeholder="Su nombre"
                className="rounded-none mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="lead-whatsapp">Numero de WhatsApp</Label>
              <div className="relative mt-1">
                <MessageCircle size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#25D366]" />
                <Input
                  data-testid="lead-whatsapp-input"
                  id="lead-whatsapp"
                  type="tel"
                  value={leadWhatsapp}
                  onChange={e => setLeadWhatsapp(e.target.value)}
                  placeholder="+593 999 307 185"
                  className="rounded-none pl-10"
                  required
                />
              </div>
              <p className="text-xs text-zinc-400 mt-1">Incluya código de país. Ej: +593 para Ecuador</p>
            </div>
            <Button
              data-testid="lead-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-none font-bold"
            >
              <MessageCircle size={16} className="mr-2" />
              {submitting ? "PROCESANDO..." : "DESCARGAR FICHA"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
